import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Typewriter from "./Typewriter";
import HeroScene from "./HeroScene";
import { hero } from "../content/home";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Header = () => {
  const [mounted, setMounted] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setMounted(true);
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMouse({ x, y });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Mouse drift sadece görselin kendi konteynerine uygulanıyor (sayfa genelinde gradient oluşturmaz)
  const visualDrift = {
    transform: `translate3d(${mouse.x * -10}px, ${mouse.y * -10}px, 0)`,
    transition: "transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    willChange: "transform",
  };

  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden">
      {/* Uniform arka plan dokusu — gradient yok */}
      <div className="pointer-events-none absolute inset-0 z-0 bg-noise opacity-[0.02] dark:opacity-[0.03]" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-6 pt-28 pb-24 sm:px-10 lg:grid-cols-[1.15fr_1fr] lg:gap-16 lg:px-12 lg:py-16">
        {/* SOL — içerik */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 px-4 py-1.5 backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="font-mono text-xs tracking-wide text-gray-800 dark:text-white/85">
              {hero.status}
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            className="font-display text-5xl font-bold leading-[0.95] tracking-tight text-gray-900 drop-shadow-[0_4px_28px_rgba(0,0,0,0.1)] sm:text-6xl lg:text-7xl xl:text-8xl dark:text-white dark:drop-shadow-[0_4px_36px_rgba(0,0,0,0.6)]"
          >
            {hero.title.first}
            <br />
            <span className="gradient-text">{hero.title.last}</span>
          </motion.h1>

          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-5 flex items-center gap-2 font-mono text-base text-gray-800 sm:text-lg dark:text-white/85"
          >
            <span className="text-brand-500">{">"}</span>
            {mounted && <Typewriter texts={hero.roles} speed={hero.typewriterSpeed} />}
            <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-brand-500" />
          </motion.div>

          {/* <motion.p
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-7 max-w-md text-balance text-base font-medium text-gray-700 sm:text-lg dark:text-white/85"
          >
            {hero.description}
          </motion.p> */}

          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            animate="show"
            className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
          >
            <a href={hero.primaryCta.href} className="btn-brand">
              {hero.primaryCta.label}
              <svg viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor">
                <path d="M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z" />
              </svg>
            </a>
            <a href={hero.secondaryCta.href} className="btn-ghost">{hero.secondaryCta.label}</a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={5}
            initial="hidden"
            animate="show"
            className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-medium text-gray-700 dark:text-white/80 lg:justify-start"
          >
            {hero.socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="rounded-md px-1.5 py-0.5 transition-colors hover:bg-black/[0.04] hover:text-gray-900 dark:hover:bg-white/10 dark:hover:text-white"
              >
                {s.label}
              </a>
            ))}
          </motion.div>
        </div>

        {/* SAĞ — 3D network globe */}
        <div className="relative mx-auto w-full max-w-[280px] sm:max-w-md lg:max-w-md">
          <div className="relative aspect-square w-full">
            <div className="absolute inset-0" style={visualDrift}>
              <HeroScene />
            </div>
          </div>
          {/* Görselin altına ince bir etiket */}
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400 dark:text-white/30">
            distributed · ai · network
          </p>
        </div>
      </div>

      <a
        href="#hakkimda"
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-gray-500 transition-colors hover:text-gray-900 dark:text-white/50 dark:hover:text-white"
      >
        <span className="grid h-10 w-7 place-items-start rounded-full border border-current p-1.5 opacity-80">
          <span className="h-2 w-1 animate-floaty rounded-full bg-current" />
        </span>
      </a>
    </section>
  );
};

export default Header;